/**
 * Comments Section Component
 *
 * A reusable component for displaying and posting comments on expenditure requests.
 * Features:
 * - Comment thread display with author avatars and timestamps
 * - New comment form with textarea and submit button
 * - Loading state while posting
 * - Auto-refresh after posting new comments
 * - Real-time comment updates
 * - @mention support for notifying specific users
 */

import { renderIcon, createSpinner } from '../utils/icons';
import { createTextarea } from './textarea';
import { apiClient } from '../services/api-client';
import { createMentionAutocomplete, insertMention } from './mention-autocomplete';
import type { DirectoryUser } from './user-search-picker';

/**
 * Comment data structure from API
 */
export interface Comment {
  id: string;
  requestId: string;
  content: string;
  createdAt: string;
  author: {
    id: string;
    displayName: string | null;
    email: string | null;
  };
}

/**
 * Options for creating the comments section
 */
export interface CommentsSectionOptions {
  /** The request ID to load comments for */
  requestId: string;
  /** Initial comments to display (optional, will fetch if not provided) */
  initialComments?: Comment[];
  /** Callback when a comment is added */
  onCommentAdded?: (comment: Comment) => void;
  /** Callback when an error occurs */
  onError?: (error: Error) => void;
  /** Whether the user can post comments (default: true) */
  canPost?: boolean;
  /** Custom placeholder text for the textarea */
  placeholder?: string;
  /** Additional CSS classes for the container */
  className?: string;
  /** Auto-refresh interval in ms (0 to disable, default: 0) */
  autoRefreshInterval?: number;
}

/**
 * Internal state for the comments section
 */
interface CommentsSectionState {
  comments: Comment[];
  isLoading: boolean;
  isPosting: boolean;
  error: string | null;
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Render comment content with @mentions styled as highlighted links
 * Converts @[Display Name](user-id) to styled span elements
 */
function renderContentWithMentions(content: string): string {
  // First escape HTML in the content
  const escaped = escapeHtml(content);

  // Then convert mentions to styled spans
  // Regex pattern to match mentions in format @[Display Name](user-id)
  const mentionPattern = /@\[([^\]]+)\]\(([^)]+)\)/g;

  return escaped.replace(mentionPattern, (_match, displayName, userId) => {
    return `<span class="mention inline-flex items-center px-1 py-0.5 rounded bg-primary/20 text-primary font-medium text-sm cursor-pointer hover:bg-primary/30 transition-colors" data-user-id="${escapeHtml(userId)}" title="@${escapeHtml(displayName)}">@${escapeHtml(displayName)}</span>`;
  });
}

/**
 * Format relative time (e.g., "5m ago", "2h ago", "3d ago")
 */
function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-CH', { month: 'short', day: 'numeric' });
}

/**
 * Format full date time for tooltip
 */
function formatFullDateTime(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-CH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Get user initials from display name
 */
function getInitials(displayName: string | null): string {
  if (!displayName) return '?';
  const parts = displayName.trim().split(' ');
  if (parts.length >= 2) {
    return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase();
  }
  return displayName.substring(0, 2).toUpperCase();
}

/**
 * Generate a consistent color based on user ID for avatar background
 */
function getAvatarColor(userId: string): string {
  // Simple hash function to generate a consistent color index
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = ((hash << 5) - hash) + userId.charCodeAt(i);
    hash = hash & hash; // Convert to 32bit integer
  }

  // Array of pleasant avatar background colors (using Tailwind classes)
  const colors = [
    'bg-primary/20 text-primary',
    'bg-purple-500/20 text-purple-400',
    'bg-blue-500/20 text-blue-400',
    'bg-green-500/20 text-green-400',
    'bg-yellow-500/20 text-yellow-400',
    'bg-orange-500/20 text-orange-400',
    'bg-pink-500/20 text-pink-400',
    'bg-cyan-500/20 text-cyan-400',
  ];

  const index = Math.abs(hash) % colors.length;
  const color = colors[index];
  return color !== undefined ? color : 'bg-primary/20 text-primary';
}

/**
 * Create an avatar element for a comment author
 */
function createCommentAvatar(author: Comment['author']): HTMLElement {
  const avatar = document.createElement('div');
  const colorClasses = getAvatarColor(author.id);
  avatar.className = `w-9 h-9 rounded-full flex items-center justify-center shrink-0 font-medium text-sm ${colorClasses}`;
  avatar.textContent = getInitials(author.displayName);
  avatar.title = author.displayName || 'Unknown user';
  return avatar;
}

/**
 * Create a single comment element
 */
function createCommentElement(comment: Comment): HTMLElement {
  const container = document.createElement('div');
  container.className = 'flex gap-3 group';
  container.dataset.commentId = comment.id;

  // Avatar
  const avatar = createCommentAvatar(comment.author);

  // Content wrapper
  const contentWrapper = document.createElement('div');
  contentWrapper.className = 'flex-1 min-w-0';

  // Header with author name and timestamp
  const header = document.createElement('div');
  header.className = 'flex items-center gap-2 mb-1';

  const authorName = document.createElement('span');
  authorName.className = 'font-medium text-foreground text-sm';
  authorName.textContent = comment.author.displayName || 'Unknown';

  const timestamp = document.createElement('span');
  timestamp.className = 'text-xs text-muted-foreground';
  timestamp.textContent = formatRelativeTime(comment.createdAt);
  timestamp.title = formatFullDateTime(comment.createdAt);

  header.appendChild(authorName);
  header.appendChild(timestamp);

  // Comment content (with @mentions styled)
  const content = document.createElement('div');
  content.className = 'text-sm text-foreground/90 whitespace-pre-wrap break-words bg-muted/30 rounded-lg px-3 py-2';
  content.innerHTML = renderContentWithMentions(comment.content);

  contentWrapper.appendChild(header);
  contentWrapper.appendChild(content);

  container.appendChild(avatar);
  container.appendChild(contentWrapper);

  return container;
}

/**
 * Create the empty state element
 */
function createEmptyState(): HTMLElement {
  const emptyState = document.createElement('div');
  emptyState.className = 'text-center py-8 text-muted-foreground';
  emptyState.innerHTML = `
    <div class="flex justify-center mb-3">
      ${renderIcon('fileText', { size: 'lg', className: 'text-muted-foreground/50' }).outerHTML}
    </div>
    <p class="text-sm">No comments yet</p>
    <p class="text-xs mt-1">Be the first to add a comment</p>
  `;
  return emptyState;
}

/**
 * Create the loading state element
 */
function createLoadingState(): HTMLElement {
  const loading = document.createElement('div');
  loading.className = 'flex items-center justify-center py-8';
  loading.innerHTML = `
    <div class="flex items-center gap-2 text-muted-foreground">
      ${createSpinner({ size: 'sm' }).outerHTML}
      <span class="text-sm">Loading comments...</span>
    </div>
  `;
  return loading;
}

/**
 * Create the error state element
 */
function createErrorState(message: string, onRetry?: () => void): HTMLElement {
  const error = document.createElement('div');
  error.className = 'text-center py-6';
  error.innerHTML = `
    <div class="flex justify-center mb-3">
      ${renderIcon('alertCircle', { size: 'lg', className: 'text-destructive' }).outerHTML}
    </div>
    <p class="text-sm text-destructive">${escapeHtml(message)}</p>
  `;

  if (onRetry) {
    const retryButton = document.createElement('button');
    retryButton.type = 'button';
    retryButton.className = 'mt-3 text-sm text-primary hover:text-primary/80 underline';
    retryButton.textContent = 'Try again';
    retryButton.addEventListener('click', onRetry);
    error.appendChild(retryButton);
  }

  return error;
}

/**
 * Create the comments section component
 */
export function createCommentsSection(options: CommentsSectionOptions): HTMLElement {
  const {
    requestId,
    initialComments,
    onCommentAdded,
    onError,
    canPost = true,
    placeholder = 'Add a comment...',
    className = '',
    autoRefreshInterval = 0,
  } = options;

  // Internal state
  const state: CommentsSectionState = {
    comments: initialComments || [],
    isLoading: !initialComments,
    isPosting: false,
    error: null,
  };

  // Create main container
  const container = document.createElement('div');
  container.className = `comments-section glass-card p-6 rounded-2xl ${className}`.trim();

  // Create header
  const header = document.createElement('div');
  header.className = 'flex items-center gap-3 mb-4';
  header.innerHTML = `
    ${renderIcon('fileText', { size: 'md', className: 'text-primary' }).outerHTML}
    <h2 class="text-lg font-semibold text-foreground">Comments</h2>
    <span class="comments-count ml-auto text-sm text-muted-foreground">${state.comments.length} comment${state.comments.length !== 1 ? 's' : ''}</span>
  `;
  container.appendChild(header);

  // Create comments list container
  const commentsList = document.createElement('div');
  commentsList.className = 'comments-list space-y-4 mb-4';
  commentsList.id = `comments-list-${requestId}`;
  container.appendChild(commentsList);

  // Create comment form (if user can post)
  let textarea: HTMLTextAreaElement | null = null;
  let submitButton: HTMLButtonElement | null = null;
  let formContainer: HTMLFormElement | null = null;
  let mentionAutocomplete: { destroy: () => void } | null = null;

  if (canPost) {
    formContainer = document.createElement('form');
    formContainer.className = 'comment-form mt-4 pt-4 border-t border-border/50';

    // Textarea wrapper (for positioning mention autocomplete)
    const textareaWrapper = document.createElement('div');
    textareaWrapper.className = 'relative mb-3';

    // Textarea
    textarea = createTextarea({
      placeholder,
      rows: 3,
      resize: 'vertical',
    });
    textareaWrapper.appendChild(textarea);

    // Attach mention autocomplete
    mentionAutocomplete = createMentionAutocomplete({
      textarea,
      onSelect: (user: DirectoryUser, startPos: number, endPos: number) => {
        if (textarea) {
          insertMention(textarea, user, startPos, endPos);
        }
      },
      minChars: 1,
      debounceMs: 200,
      maxResults: 5,
    });

    formContainer.appendChild(textareaWrapper);

    // Button container
    const buttonContainer = document.createElement('div');
    buttonContainer.className = 'flex items-center justify-between';

    // Character counter (optional hint)
    const hint = document.createElement('span');
    hint.className = 'text-xs text-muted-foreground';
    hint.textContent = 'Type @ to mention someone';

    // Submit button
    submitButton = document.createElement('button');
    submitButton.type = 'submit';
    submitButton.className = `
      inline-flex items-center gap-2 px-4 py-2
      bg-primary text-primary-foreground font-medium rounded-lg
      hover:bg-primary/90 transition-colors
      disabled:opacity-50 disabled:cursor-not-allowed
    `.replace(/\s+/g, ' ').trim();
    submitButton.innerHTML = `
      ${renderIcon('send', { size: 'sm' }).outerHTML}
      <span>Post Comment</span>
    `;

    buttonContainer.appendChild(hint);
    buttonContainer.appendChild(submitButton);
    formContainer.appendChild(buttonContainer);

    // Handle form submission
    formContainer.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!textarea || !submitButton || state.isPosting) return;

      const content = textarea.value.trim();
      if (!content) return;

      await handlePostComment(content);
    });

    container.appendChild(formContainer);
  }

  /**
   * Update the comments count in the header
   */
  function updateCommentsCount(): void {
    const countEl = container.querySelector('.comments-count');
    if (countEl) {
      countEl.textContent = `${state.comments.length} comment${state.comments.length !== 1 ? 's' : ''}`;
    }
  }

  /**
   * Render the comments list
   */
  function renderComments(): void {
    commentsList.innerHTML = '';

    if (state.isLoading) {
      commentsList.appendChild(createLoadingState());
      return;
    }

    if (state.error) {
      commentsList.appendChild(createErrorState(state.error, fetchComments));
      return;
    }

    if (state.comments.length === 0) {
      commentsList.appendChild(createEmptyState());
      return;
    }

    state.comments.forEach((comment) => {
      commentsList.appendChild(createCommentElement(comment));
    });

    updateCommentsCount();
  }

  /**
   * Add a new comment to the list (with animation)
   */
  function addCommentToList(comment: Comment): void {
    // Remove empty state if present
    const emptyState = commentsList.querySelector('.text-center');
    if (emptyState) {
      emptyState.remove();
    }

    const commentEl = createCommentElement(comment);
    commentEl.style.opacity = '0';
    commentEl.style.transform = 'translateY(-10px)';
    commentsList.appendChild(commentEl);

    // Trigger animation
    requestAnimationFrame(() => {
      commentEl.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
      commentEl.style.opacity = '1';
      commentEl.style.transform = 'translateY(0)';
    });

    state.comments.push(comment);
    updateCommentsCount();
  }

  /**
   * Set posting state
   */
  function setPostingState(isPosting: boolean): void {
    state.isPosting = isPosting;

    if (submitButton && textarea) {
      submitButton.disabled = isPosting;
      textarea.disabled = isPosting;

      if (isPosting) {
        submitButton.innerHTML = `
          ${createSpinner({ size: 'sm' }).outerHTML}
          <span>Posting...</span>
        `;
      } else {
        submitButton.innerHTML = `
          ${renderIcon('send', { size: 'sm' }).outerHTML}
          <span>Post Comment</span>
        `;
      }
    }
  }

  /**
   * Fetch comments from the API
   */
  async function fetchComments(): Promise<void> {
    state.isLoading = true;
    state.error = null;
    renderComments();

    try {
      const response = await apiClient.get<{ data: Comment[] }>(`/requests/${requestId}/comments`);
      state.comments = response.data.data || [];
      state.isLoading = false;
      renderComments();
    } catch (error) {
      state.isLoading = false;
      state.error = 'Failed to load comments';
      renderComments();
      onError?.(error instanceof Error ? error : new Error('Failed to load comments'));
    }
  }

  /**
   * Post a new comment
   */
  async function handlePostComment(content: string): Promise<void> {
    setPostingState(true);

    try {
      const response = await apiClient.post<{ data: Comment }>(`/requests/${requestId}/comments`, { content });
      const newComment = response.data.data;

      addCommentToList(newComment);

      // Clear the textarea
      if (textarea) {
        textarea.value = '';
      }

      // Notify callback
      onCommentAdded?.(newComment);
    } catch (error) {
      console.error('Failed to post comment:', error);
      onError?.(error instanceof Error ? error : new Error('Failed to post comment'));

      // Show error feedback
      if (formContainer) {
        const errorMsg = document.createElement('div');
        errorMsg.className = 'text-sm text-destructive mt-2 flex items-center gap-2';
        errorMsg.innerHTML = `
          ${renderIcon('alertCircle', { size: 'xs' }).outerHTML}
          <span>Failed to post comment. Please try again.</span>
        `;
        formContainer.appendChild(errorMsg);

        // Remove error message after 5 seconds
        setTimeout(() => {
          errorMsg.remove();
        }, 5000);
      }
    } finally {
      setPostingState(false);
    }
  }

  /**
   * Refresh comments (public method)
   */
  async function refreshComments(): Promise<void> {
    await fetchComments();
  }

  // Store refresh function on the element for external access
  (container as unknown as HTMLElement & { refreshComments: () => Promise<void> }).refreshComments = refreshComments;

  // Initial render
  renderComments();

  // Fetch comments if not provided
  if (!initialComments) {
    fetchComments();
  }

  // Set up auto-refresh if enabled
  let refreshIntervalId: number | null = null;
  if (autoRefreshInterval > 0) {
    refreshIntervalId = window.setInterval(() => {
      if (!state.isPosting) {
        fetchComments();
      }
    }, autoRefreshInterval);
  }

  // Store cleanup function (handles both auto-refresh and mention autocomplete)
  (container as HTMLElement & { cleanup?: () => void }).cleanup = () => {
    if (refreshIntervalId !== null) {
      clearInterval(refreshIntervalId);
    }
    if (mentionAutocomplete) {
      mentionAutocomplete.destroy();
    }
  };

  return container;
}

/**
 * CommentsSection class for object-oriented usage
 */
export class CommentsSection {
  private element: HTMLElement;

  constructor(options: CommentsSectionOptions) {
    this.element = createCommentsSection(options);
  }

  /**
   * Get the DOM element
   */
  getElement(): HTMLElement {
    return this.element;
  }

  /**
   * Refresh the comments list
   */
  async refresh(): Promise<void> {
    const el = this.element as HTMLElement & { refreshComments?: () => Promise<void> };
    if (el.refreshComments) {
      await el.refreshComments();
    }
  }

  /**
   * Cleanup the component (stop auto-refresh, etc.)
   */
  cleanup(): void {
    const el = this.element as HTMLElement & { cleanup?: () => void };
    if (el.cleanup) {
      el.cleanup();
    }
  }
}

export default createCommentsSection;
